import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Hero, Display3, Title, mq } from '@kvdbil/components';
import Head from '~/App/shared/components/Head';
import Section from '~/App/shared/components/Layout/Section';
import SellCarSteps from './shared/components/SellCarSteps';
import RegistrationPlateInput from '~/App/components/RegistrationPlateInput';
import { translateRoute } from '~/App/shared/localization';
import { useHistory, useLocation } from 'react-router';
import { useTranslation } from '~/Locale';
import TrustPilot from '~/App/shared/components/TrustPilot';
import { useDispatch } from 'react-redux';
import {
  sellflowErrorFallbackSolution,
  sellflowReset,
  sellflowSetRegistrationPlate,
  sellflowVehicleInfoDefaults,
  sellflowVehicleInfoSuccess
} from '~/App/shared/actions/sellflowActions';
import { createStructuredBreadcrumbs, metaHelper } from '~/helpers/seo';
import { Helmet } from 'react-helmet';
import { getBaseCanonicalUrl } from '~/helpers/baseCanonicalUrl';
import { useGA4UserIsHydrated } from '~/App/shared/hooks/useGA4User';
import { trackGA4SellCarView } from '~/helpers/client/ga4TrackEvent';
import { useDebounceGA4Event } from '~/App/shared/hooks/useDebounceGA4Event';
import {
  FaqQuestionNewRecord,
  SellFlowStepRecord,
  TitleWithTextRecord,
  SellFlowStartContentQuery,
  SellFlowStartContentDocument
} from '~/config/generated/graphql';
import SEOInfo from '~/App/shared/components/SEOInfo';
import { DarkOrLight } from '~/App/shared/interfaces/Theme';
import { getLeadObject } from '~/helpers/orchestration/sale';
import { AxiosError } from 'axios';
import HeroImage from '~/App/components/DatoCMS/HeroImage';
import { useCmsData } from '~/App/shared/hooks/useCmsData';
import { SellFlowStartPage } from '~/App/shared/interfaces/store/CmsData';
import { handleGetLeadObjectError } from '~/App/shared/actions/sellFlowAsyncActions';
import MiniFAQ from '~/App/shared/components/MiniFAQ/MiniFAQ';
import Well from '../ObjectPage/components/Well';
import BulletList from '~/App/shared/components/BulletList';

const Container = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const FormWrapper = styled.div`
  max-width: 460px;
  width: 100%;
  margin: 0 auto;
  padding: 2rem;
  background: rgba(255, 255, 255, 0.75);
`;

const HeroHeaderWrapper = styled.div`
  width: 100%;
  text-align: center;
  padding: 1rem 0.75rem;
  margin-bottom: 1.5rem;
  color: ${({ theme }) => theme.colors.text.dark};
`;

const HeroHeading = styled(Display3)<{ color?: DarkOrLight }>`
  color: ${({ color, theme }) => theme.colors.text[color || 'dark']};
`;

const HeroSubHeading = styled(Title)<{ color?: DarkOrLight }>`
  color: ${({ color, theme }) => theme.colors.text[color || 'dark']};
`;

const TrusPilotTitle = styled(Title)`
  text-align: center;
  margin-bottom: 1.5rem;
`;

const FAQContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;

  ${mq('tablet')} {
    grid-template-columns: 3fr 2fr;
  }
`;

const Heading = styled(Title)`
  margin-bottom: 1.5rem;
`;

const SellCar = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { ga4User, isHydrated } = useGA4UserIsHydrated();
  const ga4ViewEvent = useDebounceGA4Event();

  const { data: cmsData } = useCmsData<
    SellFlowStartContentQuery,
    SellFlowStartPage
  >({
    storeKey: 'sellFlowStartPage',
    query: SellFlowStartContentDocument
  });

  const schema = createStructuredBreadcrumbs([
    {
      item: `https://${t('PAGE_TITLES.TEMPLATE')}`,
      name: t('used-cars')
    },
    {
      item: `https://${t('PAGE_TITLES.TEMPLATE')}/${t('ROUTES.SELL')}`,
      name: t('ROUTES.SELL')
    }
  ]);

  useEffect(() => {
    if (isHydrated) {
      ga4ViewEvent(
        trackGA4SellCarView,
        {
          page_type: 'sell_car_page'
        },
        ga4User
      );
    }
  }, [ga4ViewEvent, ga4User, isHydrated]);

  const hero = cmsData?.startHero;

  const {
    startActionBoxText,
    startActionBoxButton,
    startSellStepsTitle,
    startSellSteps,
    startTrustpilotTitle,
    startSeoInformation,
    startSeo,
    startFaqNew,
    startCarRequirementsHeading,
    startCarRequirementsList
  } = cmsData ?? {};

  const carRequirements = useMemo(() => {
    return (
      startCarRequirementsList?.map(requirement => String(requirement.text)) ??
      []
    );
  }, [startCarRequirementsList]);

  const sellingSteps = startSellSteps;

  const canonicalUrl = getBaseCanonicalUrl(location.pathname);

  const searchParams = new URLSearchParams(location.search);
  const campaignIdentifier = searchParams.get('campaign_identifier')
    ? `&campaign_identifier=${searchParams.get('campaign_identifier')}`
    : ``;

  const onRegistrationPlateSubmit = (plate: string) => {
    setLoading(true);
    setError(null);

    dispatch(sellflowReset());
    dispatch(sellflowSetRegistrationPlate(plate));

    getLeadObject({
      params: {
        registrationPlate: plate
      }
    })
      .then(res => {
        if (res.salesFlow) {
          dispatch(sellflowVehicleInfoSuccess(res));
          dispatch(sellflowVehicleInfoDefaults(res));

          const flow = res?.salesFlow;
          const route =
            flow === 'HEAVY_EQUIPMENT'
              ? `${translateRoute(
                  t,
                  '/ROUTES.SELL_HEAVY_EQUIPMENT'
                )}?r=${plate}${campaignIdentifier}`
              : `${translateRoute(
                  t,
                  '/ROUTES.SELL/ROUTES.SELL_CAR_INFO'
                )}?r=${plate}${campaignIdentifier}`;

          history.push(route);
        } else {
          dispatch(sellflowErrorFallbackSolution());
        }
      })
      .catch((error: AxiosError) => {
        const shouldUseFallbackSolution = handleGetLeadObjectError({
          error,
          dispatch,
          action: setError,
          t
        });
        shouldUseFallbackSolution &&
          history.push(
            `${translateRoute(
              t,
              '/ROUTES.SELL/ROUTES.SELL_CAR_INFO'
            )}?r=${plate}${campaignIdentifier}`
          );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Container>
      <Head
        title={t('PAGE_TITLES.SELL_CAR')}
        meta={metaHelper({
          t,
          description: startSeo?.description,
          seoTitle: startSeo?.title,
          seoDescription: startSeo?.description,
          seoImage: startSeo?.image?.url,
          url: location.pathname
        })}
        link={[{ rel: 'canonical', href: canonicalUrl }]}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      <Hero
        imageElement={
          hero?.image?.responsiveImage && (
            <HeroImage
              responsiveImage={hero?.image?.responsiveImage}
              focalPoint={hero?.image?.focalPoint}
            />
          )
        }
      >
        <HeroHeaderWrapper>
          <HeroHeading as="h1" color={hero?.headingColor as DarkOrLight}>
            {hero?.heading}
          </HeroHeading>
          <HeroSubHeading color={hero?.subheadingColor as DarkOrLight}>
            {hero?.subheading}
          </HeroSubHeading>
        </HeroHeaderWrapper>
        <FormWrapper>
          <RegistrationPlateInput
            title={startActionBoxButton?.text || ''}
            sellingText={startActionBoxText || ''}
            onSubmit={onRegistrationPlateSubmit}
            linkToHeavyEquipment={true}
            isLoading={isLoading}
            inputProps={{
              hasError: Boolean(error),
              errors: error ? [error] : undefined
            }}
          />
        </FormWrapper>
      </Hero>
      <Section spacingTop={1.5} backgroundColor="light">
        {startTrustpilotTitle && (
          <TrusPilotTitle as="h2">{startTrustpilotTitle}</TrusPilotTitle>
        )}
        <TrustPilot type="SliderSeller" />
      </Section>
      <Section backgroundColor="light">
        <SellCarSteps
          items={sellingSteps as SellFlowStepRecord[]}
          title={startSellStepsTitle ?? ''}
        />
      </Section>
      <Section backgroundColor="gray" divider="top">
        <FAQContainer>
          <MiniFAQ questions={startFaqNew as FaqQuestionNewRecord[]} />
          <Well variant="stretchHeight" boxShadow={false}>
            <Heading as="h2">{String(startCarRequirementsHeading)}</Heading>
            <BulletList
              data-testid="faq-requirements"
              bullets={carRequirements}
            />
          </Well>
        </FAQContainer>
      </Section>

      {startSeoInformation &&
        startSeoInformation?.map((information, key) => (
          <Section key={key} divider="top" backgroundColor="light">
            <SEOInfo
              content={information?.content?.[0] as TitleWithTextRecord}
            />
          </Section>
        ))}
    </Container>
  );
};

export default SellCar;
